<template>
    <div class="dr-page">
        <!-- ===================================================== -->
        <!-- LOADING OVERLAY                                        -->
        <!-- ===================================================== -->
        <transition name="dr-fade">
            <div v-if="loading" class="dr-loading-overlay">
                <div class="dr-loading-card">
                    <div class="dr-logo dr-logo--lg">FM</div>
                    <div class="dr-loading-text">Loading drafts</div>
                    <div class="dr-loading-bar">
                        <div class="dr-loading-fill" :style="{ width: loadingProgress + '%' }"></div>
                    </div>
                </div>
            </div>
        </transition>

        <div class="dr-wrap">

            <!-- ===================================================== -->
            <!-- HERO HEADER                                            -->
            <!-- ===================================================== -->
            <header class="dr-hero">
                <div class="dr-hero-grid">
                    <div class="dr-hero-main">
                        <div class="dr-hero-label">Draft invoices</div>
                        <div class="dr-hero-title">
                            Unsubmitted Sales
                            <transition name="dr-pop">
                                <span v-if="pagination.total > 0" class="dr-count-pill">{{ pagination.total }}</span>
                            </transition>
                        </div>
                        <div class="dr-hero-tags">
                            <span class="dr-tag is-orange">
                                <b-icon icon="file-earmark-text"></b-icon> {{ pagination.total }} draft{{ pagination.total === 1 ? '' : 's' }}
                            </span>
                            <span v-if="filters.location" class="dr-tag is-teal">
                                <b-icon icon="geo-alt-fill"></b-icon> {{ getLocationLabel(filters.location) }}
                            </span>
                            <span class="dr-tag">
                                <b-icon icon="calendar-event"></b-icon> {{ getDateRangeLabel(filters.dateRange) }}
                            </span>
                        </div>
                        <p class="dr-hero-sub">
                            Pick up where you left off, review customer agreements, or send an estimate.
                        </p>
                    </div>

                    <div class="dr-hero-qr">
                        <b-icon icon="clipboard-data" font-scale="2.2"></b-icon>
                        <div>
                            <div class="dr-qr-title">Draft workflow</div>
                            <div class="dr-qr-sub">Continue editing to submit the sale.</div>
                        </div>
                    </div>
                </div>

                <div class="dr-hero-actions">
                    <b-button class="dr-btn-ghost-dark" @click="refresh" :disabled="loading">
                        <b-icon icon="arrow-clockwise"></b-icon> Refresh
                    </b-button>
                    <b-button class="dr-btn-ghost-dark" @click="goToNewSale">
                        <b-icon icon="plus-lg"></b-icon> New sale
                    </b-button>
                </div>
            </header>

            <!-- ===================================================== -->
            <!-- FILTER BAR                                             -->
            <!-- ===================================================== -->
            <section class="dr-card dr-filter">
                <div class="dr-filter-grid">
                    <label class="dr-field dr-field--search">
                        <span><b-icon icon="search"></b-icon> Search</span>
                        <b-input-group>
                            <b-form-input
                                v-model="filters.search"
                                placeholder="Invoice ID, customer name, email…"
                                @keyup.enter="loadDrafts"
                                class="dr-input"
                            ></b-form-input>
                            <b-input-group-append>
                                <b-button class="dr-btn-primary" @click="loadDrafts">
                                    <b-icon icon="search"></b-icon>
                                </b-button>
                            </b-input-group-append>
                        </b-input-group>
                    </label>

                    <label class="dr-field">
                        <span><b-icon icon="geo-alt"></b-icon> Location</span>
                        <b-form-select
                            v-model="filters.location"
                            :options="locationOptions"
                            @change="loadDrafts"
                            class="dr-select"
                        >
                            <template #first>
                                <b-form-select-option :value="null">All locations</b-form-select-option>
                            </template>
                        </b-form-select>
                    </label>

                    <label class="dr-field">
                        <span><b-icon icon="calendar-event"></b-icon> Date range</span>
                        <b-form-select
                            v-model="filters.dateRange"
                            :options="dateRangeOptions"
                            @change="loadDrafts"
                            class="dr-select"
                        ></b-form-select>
                    </label>

                    <div class="dr-field dr-field--action">
                        <b-button class="dr-btn-ghost" block @click="refresh" :disabled="loading">
                            <b-icon icon="arrow-repeat"></b-icon> Refresh
                        </b-button>
                    </div>
                </div>
            </section>

            <!-- ===================================================== -->
            <!-- EMPTY STATE                                            -->
            <!-- ===================================================== -->
            <transition name="dr-fade-up">
                <section v-if="drafts.length === 0 && !loading" class="dr-card dr-empty">
                    <div class="dr-empty-icon">
                        <b-icon icon="file-earmark-text" font-scale="2.4"></b-icon>
                    </div>
                    <h3>No draft invoices found</h3>
                    <p>
                        Start a new sale and save it as a draft to see it here.
                        Drafts let you pause a sale, review agreements, and email an estimate.
                    </p>
                    <b-button class="dr-btn-primary" @click="goToNewSale">
                        <b-icon icon="plus-lg"></b-icon> Create new sale
                    </b-button>
                </section>
            </transition>

            <!-- ===================================================== -->
            <!-- DRAFT CARDS (replaces table + mobile card list)         -->
            <!-- ===================================================== -->
            <transition-group name="dr-list" tag="div" class="dr-list" v-if="drafts.length > 0">
                <article
                    v-for="(draft, index) in drafts"
                    :key="draft.draft_id"
                    class="dr-pcard"
                    :class="{
                        'is-complete': getProgressPercentage(draft) >= 75,
                        'is-warning': getProgressPercentage(draft) >= 25 && getProgressPercentage(draft) < 75,
                        'is-started': getProgressPercentage(draft) < 25,
                    }"
                    :style="{ animationDelay: (index * 0.03) + 's' }"
                >
                    <!-- ===== TOP: IDENTITY ===== -->
                    <div class="dr-pcard-top">
                        <div class="dr-pcard-id">
                            <span class="dr-pcard-id-icon"><b-icon icon="receipt"></b-icon></span>
                            <div>
                                <div class="dr-pcard-id-label">Draft ID</div>
                                <div class="dr-pcard-id-value">{{ draft.draft_id }}</div>
                            </div>
                        </div>

                        <div class="dr-pcard-badges">
                            <span class="dr-chip is-teal">
                                <b-icon icon="box-seam"></b-icon> {{ draft.items_count }} item{{ draft.items_count === 1 ? '' : 's' }}
                            </span>
                            <span class="dr-chip is-orange">
                                <b-icon icon="cash-coin"></b-icon> ${{ Number(draft.total || 0).toFixed(2) }}
                            </span>
                            <span
                                class="dr-chip"
                                :class="(draft.data && draft.data.terms_agreed) ? 'is-teal' : 'is-warn'"
                            >
                                <b-icon :icon="(draft.data && draft.data.terms_agreed) ? 'check-circle-fill' : 'exclamation-circle-fill'"></b-icon>
                                Terms
                            </span>
                            <span v-if="draft.data && draft.data.has_signature" class="dr-chip is-teal">
                                <b-icon icon="pen"></b-icon> Signed
                            </span>
                        </div>
                    </div>

                    <!-- ===== MIDDLE: META GRID ===== -->
                    <div class="dr-pcard-body">
                        <!-- Customer column -->
                        <div class="dr-pcard-panel dr-pcard-customer">
                            <div class="dr-panel-kicker">
                                <b-icon icon="person"></b-icon> Customer
                            </div>
                            <template v-if="draft.customer && Object.keys(draft.customer).length > 0">
                                <div class="dr-panel-title">
                                    {{ draft.customer.fullname || 'No name' }}
                                </div>
                                <div class="dr-panel-line">
                                    <b-icon icon="envelope"></b-icon>
                                    {{ draft.customer.email || '—' }}
                                </div>
                                <div class="dr-panel-line">
                                    <b-icon icon="telephone"></b-icon>
                                    {{ draft.customer.phone || '—' }}
                                </div>
                                <div
                                    v-if="draft.customer.address_line_1 || draft.customer.city"
                                    class="dr-panel-line"
                                >
                                    <b-icon icon="geo-alt"></b-icon>
                                    {{ formatAddress(draft.customer) }}
                                </div>
                            </template>
                            <div v-else class="dr-panel-empty">
                                <b-icon icon="person-dash"></b-icon>
                                No customer info
                            </div>
                        </div>

                        <!-- Meta column: created by, dates, location -->
                        <div class="dr-pcard-panel dr-pcard-meta">
                            <div class="dr-meta-row">
                                <span><b-icon icon="person-badge"></b-icon> Created by</span>
                                <b>{{ draft.username || '—' }}</b>
                            </div>
                            <div class="dr-meta-row">
                                <span><b-icon icon="geo-alt-fill"></b-icon> Location</span>
                                <b>{{ draft.location || '—' }}</b>
                            </div>
                            <div class="dr-meta-row">
                                <span><b-icon icon="calendar-plus"></b-icon> Created</span>
                                <b>{{ formatDate(draft.created_at) }}</b>
                            </div>
                            <div class="dr-meta-row">
                                <span><b-icon icon="clock-history"></b-icon> Last modified</span>
                                <b>{{ formatDate(draft.last_modified) }}</b>
                            </div>
                        </div>

                        <!-- Progress column -->
                        <div class="dr-pcard-panel dr-pcard-progress">
                            <div class="dr-panel-kicker">
                                <b-icon icon="speedometer2"></b-icon> Progress
                            </div>
                            <div class="dr-progress-ring">
                                <svg viewBox="0 0 42 42" class="dr-progress-svg">
                                    <circle class="dr-progress-track" cx="21" cy="21" r="16"></circle>
                                    <circle
                                        class="dr-progress-fill-circle"
                                        :class="'is-' + getProgressVariant(draft)"
                                        cx="21" cy="21" r="16"
                                        :style="{ strokeDashoffset: progressOffset(draft) }"
                                    ></circle>
                                </svg>
                                <div class="dr-progress-value">{{ Math.round(getProgressPercentage(draft)) }}%</div>
                            </div>
                            <div class="dr-progress-label">{{ getProgressText(draft) }}</div>
                            <div class="dr-progress-sub">
                                {{ getProgressDoneCount(draft) }} of 5 sections done
                            </div>
                        </div>

                        <!-- Agreements column -->
                        <div class="dr-pcard-panel dr-pcard-agreements">
                            <div class="dr-panel-kicker">
                                <b-icon icon="shield-check"></b-icon> Agreements
                            </div>
                            <div class="dr-agreement-list">
                                <span class="dr-agreement-item" :class="(draft.data && draft.data.terms_agreed) ? 'is-ok' : 'is-no'">
                                    <b-icon :icon="(draft.data && draft.data.terms_agreed) ? 'check-lg' : 'x-lg'"></b-icon>
                                    Terms
                                </span>
                                <span class="dr-agreement-item" :class="(draft.data && draft.data.has_signature) ? 'is-ok' : 'is-no'">
                                    <b-icon :icon="(draft.data && draft.data.has_signature) ? 'check-lg' : 'x-lg'"></b-icon>
                                    Signature
                                </span>
                                <span v-if="draft.data && draft.data.warranty_refused" class="dr-agreement-item is-ok">
                                    <b-icon icon="check-lg"></b-icon> Warranty refused
                                </span>
                                <span v-if="draft.data && draft.data.delivery_waiver_agreed" class="dr-agreement-item is-ok">
                                    <b-icon icon="check-lg"></b-icon> Delivery waiver
                                </span>
                                <span v-if="draft.data && draft.data.space_preparation_agreed" class="dr-agreement-item is-ok">
                                    <b-icon icon="check-lg"></b-icon> Space prep
                                </span>
                                <span v-if="draft.data && draft.data.address_confirmation_agreed" class="dr-agreement-item is-ok">
                                    <b-icon icon="check-lg"></b-icon> Address confirmed
                                </span>
                                <span
                                    v-if="!hasAnyAgreement(draft)"
                                    class="dr-agreement-empty"
                                >
                                    No agreements recorded
                                </span>
                            </div>
                        </div>
                    </div>

                    <!-- ===== BOTTOM: ACTIONS ===== -->
                    <div class="dr-pcard-actions">
                        <div class="dr-actions-primary">
                            <b-button class="dr-btn-primary" @click="continueEditing(draft)">
                                <b-icon icon="pencil"></b-icon> Continue editing
                            </b-button>
                            <b-button class="dr-btn-ghost" @click="viewEstimatedInvoice(draft)">
                                <b-icon icon="file-earmark-text"></b-icon> Preview invoice
                            </b-button>
                        </div>

                        <b-dropdown
                            right
                            variant="link"
                            toggle-class="dr-more-toggle"
                            no-caret
                            menu-class="dr-dropdown-menu"
                            class="dr-more"
                        >
                            <template #button-content>
                                <b-icon icon="three-dots-vertical"></b-icon>
                            </template>
                            <b-dropdown-item @click="viewDraft(draft)">
                                <b-icon icon="eye" class="mr-2"></b-icon> View summary
                            </b-dropdown-item>
                            <b-dropdown-item @click="openCustomerInfoTab(draft)">
                                <b-icon icon="box-arrow-up-right" class="mr-2"></b-icon> Customer info &amp; agreement
                            </b-dropdown-item>
                            <b-dropdown-divider></b-dropdown-divider>
                            <b-dropdown-item variant="danger" @click="deleteDraft(draft)">
                                <b-icon icon="trash" class="mr-2"></b-icon> Delete draft
                            </b-dropdown-item>
                        </b-dropdown>
                    </div>
                </article>
            </transition-group>

            <!-- ===================================================== -->
            <!-- PAGINATION                                             -->
            <!-- ===================================================== -->
            <transition name="dr-fade-up">
                <section v-if="drafts.length > 0" class="dr-card dr-pagination-wrap">
                    <div class="dr-pagination-info">
                        <b-icon icon="list-ol"></b-icon>
                        Showing {{ drafts.length }} of {{ pagination.total }} draft{{ pagination.total === 1 ? '' : 's' }}
                    </div>
                    <b-pagination
                        v-model="pagination.page"
                        :total-rows="pagination.total"
                        :per-page="pagination.perPage"
                        @change="loadDrafts"
                        :hide-goto-end-buttons="isMobile"
                        class="dr-pagination"
                    ></b-pagination>
                </section>
            </transition>
        </div>

        <!-- ===================================================== -->
        <!-- ESTIMATED INVOICE PREVIEW MODAL                        -->
        <!-- ===================================================== -->
        <b-modal
            id="modal-estimated-invoice-preview"
            size="xl"
            hide-footer
            scrollable
            centered
            content-class="dr-modal"
            body-class="p-0"
            header-class="dr-modal-header"
        >
            <template #modal-header>
                <div class="dr-modal-title">
                    <span class="dr-modal-title-icon"><b-icon icon="exclamation-triangle-fill"></b-icon></span>
                    <div>
                        <div class="dr-modal-title-main">Estimated invoice preview</div>
                        <div class="dr-modal-title-sub">This is an estimate, not a final bill.</div>
                    </div>
                </div>
            </template>

            <transition name="dr-fade">
                <div v-if="selectedDraft" class="dr-modal-agreement">
                    <h6 class="dr-modal-agreement-title">
                        <b-icon icon="handshake"></b-icon> Agreement status
                    </h6>
                    <div class="dr-modal-agreement-grid">
                        <div class="dr-modal-agreement-item">
                            <span class="dr-chip" :class="(selectedDraft.data && selectedDraft.data.terms_agreed) ? 'is-teal' : 'is-warn'">
                                <b-icon :icon="(selectedDraft.data && selectedDraft.data.terms_agreed) ? 'check-circle-fill' : 'exclamation-circle-fill'"></b-icon>
                                Terms &amp; Conditions
                            </span>
                            <small>{{ (selectedDraft.data && selectedDraft.data.terms_agreed) ? 'Agreed' : 'Not agreed' }}</small>
                        </div>
                        <div class="dr-modal-agreement-item">
                            <span class="dr-chip" :class="(selectedDraft.data && selectedDraft.data.warranty_refused) ? 'is-teal' : ''">
                                <b-icon icon="shield-slash"></b-icon> Warranty
                            </span>
                            <small>{{ (selectedDraft.data && selectedDraft.data.warranty_refused) ? 'Refused' : 'Not refused' }}</small>
                        </div>
                        <div class="dr-modal-agreement-item">
                            <span class="dr-chip" :class="(selectedDraft.data && selectedDraft.data.delivery_waiver_agreed) ? 'is-teal' : ''">
                                <b-icon icon="truck"></b-icon> Delivery waiver
                            </span>
                            <small>{{ (selectedDraft.data && selectedDraft.data.delivery_waiver_agreed) ? 'Waived' : 'Standard' }}</small>
                        </div>
                        <div class="dr-modal-agreement-item">
                            <span class="dr-chip" :class="(selectedDraft.data && selectedDraft.data.space_preparation_agreed) ? 'is-teal' : ''">
                                <b-icon icon="house"></b-icon> Space prep
                            </span>
                            <small>{{ (selectedDraft.data && selectedDraft.data.space_preparation_agreed) ? 'Acknowledged' : 'Not acknowledged' }}</small>
                        </div>
                    </div>

                    <div class="dr-modal-signature">
                        <span class="dr-chip" :class="(selectedDraft.data && selectedDraft.data.has_signature) ? 'is-teal' : 'is-danger'">
                            <b-icon icon="pen"></b-icon>
                            {{ (selectedDraft.data && selectedDraft.data.has_signature) ? 'Customer signed' : 'No signature' }}
                        </span>
                        <small v-if="selectedDraft.data && selectedDraft.data.signature_captured_at">
                            Signed {{ formatDate(selectedDraft.data.signature_captured_at) }}
                        </small>
                    </div>
                </div>
            </transition>

            <EstimatedInvoicePreview
                v-if="selectedDraft"
                :invoiceData="prepareInvoiceData(selectedDraft)"
                :locationData="getLocationDetails(selectedDraft.location_id)"
                :policyText="policyText"
                :agreementData="getAgreementData(selectedDraft)"
                @close="$bvModal.hide('modal-estimated-invoice-preview')"
                :key="modalKey"
            />

            <div v-else class="dr-modal-loading">
                <b-spinner variant="primary"></b-spinner>
                <p>Loading invoice data…</p>
            </div>
        </b-modal>
    </div>
</template>

<script>
import moment from 'moment'
import Requestor from '@/utilities/Requestor'
import EstimatedInvoicePreview from '@/components/Sales/EstimatedInvoicePreview.vue'

export default {
    name: 'ListDraftInvoices',
    components: { EstimatedInvoicePreview },
    data() {
        return {
            loading: false,
            drafts: [],
            sortBy: 'last_modified',
            sortDesc: true,
            loadingProgress: 0,
            allowed_states: [
                { value: 'PA', text: 'PENNSYLVANIA' },
                { value: 'NJ', text: 'NEW JERSEY' },
                { value: 'DE', text: 'DELAWARE' },
                { value: 'AL', text: 'ALABAMA' },
                { value: 'AK', text: 'ALASKA' },
                { value: 'AS', text: 'AMERICAN SAMOA' },
                { value: 'AZ', text: 'ARIZONA' },
                { value: 'AR', text: 'ARKANSAS' },
                { value: 'CA', text: 'CALIFORNIA' },
                { value: 'CO', text: 'COLORADO' },
                { value: 'CT', text: 'CONNECTICUT' },
                { value: 'DC', text: 'DISTRICT OF COLUMBIA' },
                { value: 'FM', text: 'FEDERATED STATES OF MICRONESIA' },
                { value: 'FL', text: 'FLORIDA' },
                { value: 'GA', text: 'GEORGIA' },
                { value: 'GU', text: 'GUAM' },
                { value: 'HI', text: 'HAWAII' },
                { value: 'ID', text: 'IDAHO' },
                { value: 'IL', text: 'ILLINOIS' },
                { value: 'IN', text: 'INDIANA' },
                { value: 'IA', text: 'IOWA' },
                { value: 'KS', text: 'KANSAS' },
                { value: 'KY', text: 'KENTUCKY' },
                { value: 'LA', text: 'LOUISIANA' },
                { value: 'ME', text: 'MAINE' },
                { value: 'MH', text: 'MARSHALL ISLANDS' },
                { value: 'MD', text: 'MARYLAND' },
                { value: 'MA', text: 'MASSACHUSETTS' },
                { value: 'MI', text: 'MICHIGAN' },
                { value: 'MS', text: 'MISSISSIPPI' },
                { value: 'MO', text: 'MISSOURI' },
                { value: 'MT', text: 'MONTANA' },
                { value: 'NE', text: 'NEBRASKA' },
                { value: 'NV', text: 'NEVADA' },
                { value: 'NH', text: 'NEW HAMPSHIRE' },
                { value: 'NM', text: 'NEW MEXICO' },
                { value: 'NY', text: 'NEW YORK' },
                { value: 'NC', text: 'NORTH CAROLINA' },
                { value: 'ND', text: 'NORTH DAKOTA' },
                { value: 'MP', text: 'NORTHERN MARIANA ISLANDS' },
                { value: 'OH', text: 'OHIO' },
                { value: 'OK', text: 'OKLAHOMA' },
                { value: 'OR', text: 'OREGON' },
                { value: 'PW', text: 'PALAU' },
                { value: 'PR', text: 'PUERTO RICO' },
                { value: 'RI', text: 'RHODE ISLAND' },
                { value: 'SC', text: 'SOUTH CAROLINA' },
                { value: 'SD', text: 'SOUTH DAKOTA' },
                { value: 'TN', text: 'TENNESSEE' },
                { value: 'TX', text: 'TEXAS' },
                { value: 'UT', text: 'UTAH' },
                { value: 'VT', text: 'VERMONT' },
                { value: 'VA', text: 'VIRGINIA' },
                { value: 'WA', text: 'WASHINGTON' },
                { value: 'WV', text: 'WEST VIRGINIA' },
                { value: 'WI', text: 'WISCONSIN' },
                { value: 'WY', text: 'WYOMING' },
            ],
            zipcodes: [],
            policyText: '',
            policyTextSpanish: '',
            filters: {
                search: '',
                location: null,
                dateRange: 'today',
            },
            locationOptions: [],
            dateRangeOptions: [
                { value: 'today', text: 'Today' },
                { value: 'yesterday', text: 'Yesterday' },
                { value: 'week', text: 'This week' },
                { value: 'month', text: 'This month' },
                { value: 'all', text: 'All time' },
            ],
            pagination: {
                page: 1,
                perPage: 20,
                total: 0,
            },
            packet: {},
            selectedDraft: null,
            modalKey: 0,
            locationDetailsCache: {},
            isMobile: false,
            loadingInterval: null,
            msgInterval: null,
        }
    },
    computed: {
        userDetails() {
            return JSON.parse(localStorage.userDetails || '{}')
        },
    },
    methods: {
        // ==================== LOADER ====================
        startLoadingAnimation() {
            this.loadingProgress = 0
            this.loadingInterval = setInterval(() => {
                if (this.loadingProgress < 90) {
                    this.loadingProgress += Math.random() * 10
                    if (this.loadingProgress > 90) this.loadingProgress = 90
                }
            }, 300)
        },
        stopLoadingAnimation(success = true) {
            if (this.loadingInterval) clearInterval(this.loadingInterval)
            if (success) {
                this.loadingProgress = 100
                setTimeout(() => { this.loading = false }, 500)
            } else {
                this.loading = false
            }
        },
        checkMobile() {
            this.isMobile = window.innerWidth < 768
        },

        // ==================== HELPERS ====================
        getLocationLabel(value) {
            const loc = this.locationOptions.find(l => l.value === value)
            return loc ? loc.text : value
        },
        getDateRangeLabel(value) {
            const opt = this.dateRangeOptions.find(o => o.value === value)
            return opt ? opt.text : value
        },
        formatAddress(customer) {
            if (!customer) return ''
            const parts = [
                customer.address_line_1,
                customer.city,
                customer.state,
                customer.zipcode,
            ].filter(Boolean)
            return parts.join(', ')
        },
        hasAnyAgreement(draft) {
            if (!draft || !draft.data) return false
            const d = draft.data
            return !!(
                d.terms_agreed ||
                d.has_signature ||
                d.warranty_refused ||
                d.delivery_waiver_agreed ||
                d.space_preparation_agreed ||
                d.address_confirmation_agreed
            )
        },
        progressOffset(draft) {
            const circumference = 2 * Math.PI * 16
            const pct = this.getProgressPercentage(draft) / 100
            return circumference * (1 - pct)
        },
        getProgressDoneCount(draft) {
            const fields = ['customer', 'items', 'charges', 'payment', 'agreement']
            let filled = 0
            const draftData = draft.data || {}

            if (draftData.customer && Object.keys(draftData.customer).length > 0) filled++
            if (draftData.items && draftData.items.length > 0) filled++
            if (
                (draftData.delivery_charges && draftData.delivery_charges > 0) ||
                (draftData.setup_charges && draftData.setup_charges > 0) ||
                (draftData.warranty_charges && draftData.warranty_charges > 0)
            ) filled++
            if (draftData.payment_methods && draftData.payment_methods.length > 0) filled++

            let agreementCount = 0
            if (draftData.terms_agreed === true) agreementCount++
            if (draftData.has_signature === true) agreementCount++
            if (draftData.warranty_refused !== undefined) agreementCount++
            if (draftData.delivery_waiver_agreed !== undefined) agreementCount++
            if (draftData.space_preparation_agreed !== undefined) agreementCount++

            if (agreementCount >= 3) filled++
            return filled
        },

        // ==================== NAVIGATION ====================
        goToNewSale() {
            this.$router.push({ path: '/sales/new/' }).catch(() => {})
        },
        continueEditing(draft) {
            this.$router.push({
                name: 'NewSale',
                query: { draft_id: draft.draft_id },
            }).catch(() => {
                this.$router.push({
                    path: '/sales/new/',
                    query: { draft_id: draft.draft_id },
                })
            })
        },
        openCustomerInfoTab(draft) {
            const baseUrl = window.location.origin
            const route = '/sales/draft-customer-info'
            const url = `${baseUrl}/#${route}?draftId=${draft.draft_id}&t=${Date.now()}`

            const newWindow = window.open(url, '_blank')
            if (!newWindow) {
                this.$bvToast.toast('Popup blocked! Please allow popups for this site.', {
                    title: 'Error',
                    variant: 'danger',
                    autoHideDelay: 5000,
                })
                return
            }

            this.$bvToast.toast('Opening customer information in new tab…', {
                title: 'Info',
                variant: 'info',
                autoHideDelay: 2000,
            })
        },

        // ==================== DATA LOADING ====================
        loadZipcodes() {
            const path = '/api/sales/get_all_zipcodes'
            const that = this
            Requestor.getAsyncP(path, {}, this,
                function (response) {
                    if (response.data.result) that.zipcodes = response.data.result
                },
                function (error) { console.error('Error loading zipcodes:', error) }
            )
        },
        fetchPolicy() {
            const that = this
            const pathEn = '/api/policy/get_policy/'
            const sEn = function (response) {
                if (response.data.result && response.data.result.length > 0) {
                    that.policyText = response.data.result[0].text
                } else {
                    that.policyText = '<p>Please read and agree to our standard terms and conditions.</p>'
                }
            }
            const pathEs = '/api/policy/get_policy/'
            const sEs = function (response) {
                if (response.data.result && response.data.result.length > 0) {
                    that.policyTextSpanish = response.data.result[0].text
                } else {
                    that.policyTextSpanish = '<p>Por favor, lea y acepte nuestros términos y condiciones estándar.</p>'
                }
            }
            const f = function (error) {
                console.error('Error loading policy:', error)
                that.policyText = '<p>Please read and agree to our standard terms and conditions.</p>'
                that.policyTextSpanish = '<p>Por favor, lea y acepte nuestros términos y condiciones estándar.</p>'
            }
            Requestor.getAsyncP(pathEn, { language: 'en' }, this, sEn, f)
            Requestor.getAsyncP(pathEs, { language: 'es' }, this, sEs, f)
        },

        // ==================== FORMATTERS ====================
        formatDate(date) {
            return date ? moment(date).format('MM/DD/YYYY hh:mm A') : 'N/A'
        },

        // ==================== PROGRESS ====================
        getProgressPercentage(draft) {
            const fields = ['customer', 'items', 'charges', 'payment', 'agreement']
            let filled = 0
            const draftData = draft.data || {}

            if (draftData.customer && Object.keys(draftData.customer).length > 0) filled++
            if (draftData.items && draftData.items.length > 0) filled++
            if (
                (draftData.delivery_charges && draftData.delivery_charges > 0) ||
                (draftData.setup_charges && draftData.setup_charges > 0) ||
                (draftData.warranty_charges && draftData.warranty_charges > 0)
            ) filled++
            if (draftData.payment_methods && draftData.payment_methods.length > 0) filled++

            let agreementCount = 0
            if (draftData.terms_agreed === true) agreementCount++
            if (draftData.has_signature === true) agreementCount++
            if (draftData.warranty_refused !== undefined) agreementCount++
            if (draftData.delivery_waiver_agreed !== undefined) agreementCount++
            if (draftData.space_preparation_agreed !== undefined) agreementCount++

            if (agreementCount >= 3) filled++
            return (filled / fields.length) * 100
        },
        getProgressVariant(draft) {
            const progress = this.getProgressPercentage(draft)
            if (progress >= 75) return 'success'
            if (progress >= 50) return 'info'
            if (progress >= 25) return 'warning'
            return 'danger'
        },
        getProgressText(draft) {
            const progress = this.getProgressPercentage(draft)
            if (progress >= 75) return 'Almost complete'
            if (progress >= 50) return 'Halfway there'
            if (progress >= 25) return 'Started'
            return 'Just started'
        },

        // ==================== LOAD DRAFTS ====================
        loadDrafts() {
            this.loading = true
            this.startLoadingAnimation()
            const path = '/api/sales/list_draft_invoices/'
            const data = {
                ...this.filters,
                page: this.pagination.page,
                per_page: this.pagination.perPage,
                username: this.userDetails.username,
                location_id: this.userDetails.properties.location_id || null,
                is_admin: this.userDetails.is_admin || false,
            }
            const that = this
            const s = function (response) {
                if (response.data.error) {
                    that.$bvToast.toast(response.data.error, { title: 'Error', variant: 'warning' })
                    that.drafts = []
                } else if (response.data.result) {
                    that.drafts = response.data.result.drafts || []
                    that.pagination.total = response.data.result.total || 0
                }
                that.stopLoadingAnimation(true)
            }
            const f = function (error) {
                console.error('Error loading drafts:', error)
                that.$bvToast.toast('Error loading drafts', { title: 'Error', variant: 'danger' })
                that.stopLoadingAnimation(false)
                that.drafts = []
            }
            Requestor.postSyncP(path, data, this, s, f)
        },
        refresh() {
            this.pagination.page = 1
            this.loadDrafts()
        },

        // ==================== MODALS / SUMMARY ====================
        viewDraft(draft) {
            this.$bvModal.msgBoxOk(this.formatDraftSummary(draft), {
                title: 'Draft summary',
                size: 'lg',
                okTitle: 'Close',
                scrollable: true,
            })
        },
        formatDraftSummary(draft) {
            const data = draft.data || {}
            const customer = data.customer || {}
            const items = data.items || []

            let agreementStatus = ''
            if (data.terms_agreed) agreementStatus += '✓ Terms agreed '
            if (data.warranty_refused) agreementStatus += '| Warranty refused '
            if (data.delivery_waiver_agreed) agreementStatus += '| Delivery waiver '
            if (data.space_preparation_agreed) agreementStatus += '| Space prep acknowledged '
            if (data.address_confirmation_agreed) agreementStatus += '| ✓ Address confirmed '
            if (data.has_signature) agreementStatus += '| Signed '

            let summary = `
                <div style="font-family: monospace; padding: 10px;">
                    <h4>Draft invoice: ${draft.draft_id}</h4>
                    <hr>
                    <h5>Customer information</h5>
                    <p>
                        <strong>Name:</strong> ${customer.fullname || 'N/A'}<br>
                        <strong>Email:</strong> ${customer.email || 'N/A'}<br>
                        <strong>Phone:</strong> ${customer.phone || 'N/A'}<br>
                        <strong>Address:</strong> ${customer.address_line_1 || ''} ${customer.city || ''} ${customer.state || ''} ${customer.zipcode || ''}
                    </p>
                    <hr>
                    <h5>Agreement status</h5>
                    <p>${agreementStatus || 'No agreements recorded'}</p>
                    <hr>
                    <h5>Items (${items.length})</h5>
                    <table style="width: 100%; border-collapse: collapse;">
                        <thead>
                            <tr style="background: #f0f0f0;">
                                <th style="padding: 5px; border: 1px solid #ddd;">SKU</th>
                                <th style="padding: 5px; border: 1px solid #ddd;">Product</th>
                                <th style="padding: 5px; border: 1px solid #ddd;">Qty</th>
                                <th style="padding: 5px; border: 1px solid #ddd;">Price</th>
                            </tr>
                        </thead>
                        <tbody>
            `
            items.forEach(item => {
                summary += `
                            <tr>
                                <td style="padding: 5px; border: 1px solid #ddd;">${item.product_sku || ''}</td>
                                <td style="padding: 5px; border: 1px solid #ddd;">${item.product_name || ''}</td>
                                <td style="padding: 5px; border: 1px solid #ddd; text-align: center;">${item.quantity || 0}</td>
                                <td style="padding: 5px; border: 1px solid #ddd; text-align: right;">$${((item.selling_price || 0) * (item.quantity || 0)).toFixed(2)}</td>
                            </tr>
                `
            })
            summary += `
                        </tbody>
                    </table>
                    <hr>
                    <p style="text-align: right;">
                        <strong>Subtotal:</strong> $${(data.subtotal || 0).toFixed(2)}<br>
                        <strong>Total:</strong> $${(data.total || 0).toFixed(2)}
                    </p>
                </div>
            `
            return summary
        },

        deleteDraft(draft) {
            this.$bvModal.msgBoxConfirm(`Delete draft invoice ${draft.draft_id}?`, {
                title: 'Confirm delete',
                size: 'sm',
                buttonSize: 'sm',
                okVariant: 'danger',
                okTitle: 'DELETE',
                cancelTitle: 'Cancel',
                footerClass: 'p-2',
                hideHeaderClose: false,
                centered: true,
            }).then(value => {
                if (value) this.performDelete(draft.draft_id)
            })
        },
        performDelete(draftId) {
            const path = '/api/sales/delete_draft_invoice/'
            const data = { draft_id: draftId }
            const that = this
            const s = function (response) {
                if (response.data.error) {
                    that.$bvToast.toast(response.data.error, { title: 'Error', variant: 'warning' })
                } else {
                    that.$bvToast.toast('Draft deleted successfully', { title: 'Success', variant: 'success' })
                    that.loadDrafts()
                }
            }
            const f = function (error) {
                console.error('Error deleting draft:', error)
                that.$bvToast.toast('Error deleting draft', { title: 'Error', variant: 'danger' })
            }
            Requestor.postSyncP(path, data, this, s, f)
        },

        // ==================== ESTIMATED INVOICE ====================
        viewEstimatedInvoice(draft) {
            this.selectedDraft = draft
            this.modalKey++
            this.loadPolicyText()
            this.$bvModal.show('modal-estimated-invoice-preview')
        },
        getAgreementData(draft) {
            if (!draft || !draft.data) return {}
            return {
                terms_agreed: draft.data.terms_agreed || false,
                warranty_refused: draft.data.warranty_refused || false,
                delivery_waiver_agreed: draft.data.delivery_waiver_agreed || false,
                space_preparation_agreed: draft.data.space_preparation_agreed || false,
                address_confirmation_agreed: draft.data.address_confirmation_agreed || false,
                has_signature: draft.data.has_signature || false,
                policy_language: draft.data.policy_language || 'en',
                policy_agreed_at: draft.data.policy_agreed_at || null,
                signature_captured_at: draft.data.signature_captured_at || null,
            }
        },
        prepareInvoiceData(draft) {
            if (!draft) return {}
            const draftData = draft.data || {}
            return {
                estimate_id: draft.draft_id,
                draft_id: draft.draft_id,
                created_at: draft.created_at,
                created_by: draft.created_by || this.userDetails.username,
                location_id: draft.location_id,
                customer: draftData.customer || {},
                items: draftData.items || [],
                subtotal: draftData.subtotal || 0,
                delivery_charges: draftData.delivery_charges || 0,
                setup_charges: draftData.setup_charges || 0,
                warranty_charges: draftData.warranty_charges || 0,
                taxable_subtotal: draftData.taxable_subtotal || 0,
                tax_perc: draftData.tax_perc || 0.07,
                sales_tax: draftData.sales_tax || 0,
                total: draftData.total || 0,
                payment_methods: draftData.payment_methods || [],
                is_layaway: draftData.is_layaway || false,
                terms_agreed: draftData.terms_agreed || false,
                warranty_refused: draftData.warranty_refused || false,
                delivery_waiver_agreed: draftData.delivery_waiver_agreed || false,
                space_preparation_agreed: draftData.space_preparation_agreed || false,
                address_confirmation_agreed: draftData.address_confirmation_agreed || false,
                has_signature: draftData.has_signature || false,
                signature_url: draftData.signature_url || null,
                signature_captured_at: draftData.signature_captured_at || null,
            }
        },
        loadPolicyText() {
            const path = '/api/policy/get_policy/'
            const that = this
            const s = function (response) {
                if (response.data.result && response.data.result.length > 0) {
                    that.policyText = response.data.result[0].text
                } else {
                    that.policyText = '<p>Standard terms and conditions apply. Please contact store for complete details.</p>'
                }
            }
            const f = function (error) {
                console.error('Error loading policy:', error)
                that.policyText = '<p>Terms and conditions available at store.</p>'
            }
            Requestor.getAsyncP(path, {}, this, s, f)
        },
        getLocationDetails(locationId) {
            if (!locationId) return {}
            if (this.locationDetailsCache[locationId]) return this.locationDetailsCache[locationId]

            try {
                const location = this.locationOptions.find(l => l.value == locationId)
                if (location) {
                    const details = {
                        address: location.address || '',
                        email: location.email || '',
                        phone: location.phone || '',
                        short_name: location.short_name || '',
                        website: 'www.myfurnituremecca.com',
                    }
                    this.$set(this.locationDetailsCache, locationId, details)
                    return details
                }
            } catch (e) {
                console.error('Error getting location details:', e)
            }
            return {
                address: '',
                email: '',
                phone: '',
                short_name: '',
                website: 'www.myfurnituremecca.com',
            }
        },
        loadLocations() {
            if (localStorage.packet) {
                try {
                    this.packet = JSON.parse(localStorage.packet)
                    this.locationOptions = (this.packet.locations || []).map(loc => ({
                        value: loc.value,
                        text: loc.text,
                        address: loc.address,
                        email: loc.email,
                        phone: loc.phone,
                        short_name: loc.short_name,
                    }))
                } catch (e) {
                    console.error('Error parsing packet:', e)
                    this.locationOptions = []
                }
            }
        },
    },

    mounted() {
        this.checkMobile()
        window.addEventListener('resize', this.checkMobile)
        this.loadLocations()
        this.loadDrafts()
        this.loadZipcodes()
        this.fetchPolicy()
    },

    beforeDestroy() {
        window.removeEventListener('resize', this.checkMobile)
        if (this.loadingInterval) clearInterval(this.loadingInterval)
        if (this.msgInterval) clearInterval(this.msgInterval)
    },
}
</script>

<style scoped>
/* ===================================================== */
/* PAGE SHELL                                              */
/* ===================================================== */
.dr-page {
    position: relative;
    background: #F6F4F0;
    min-height: 100vh;
    padding-bottom: 60px;
    font-family: 'Manrope', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
    color: #221F1C;
    font-size: 15px;
}
.dr-wrap {
    max-width: 1480px;
    width: 94%;
    margin: 0 auto;
    padding-top: 18px;
}

@media (max-width: 767px) {
    .dr-wrap { width: 100%; padding: 10px 12px 0; }
}

/* ===================================================== */
/* LOADING OVERLAY                                         */
/* ===================================================== */
.dr-loading-overlay {
    position: fixed;
    inset: 0;
    z-index: 9999;
    background: rgba(34, 31, 28, 0.82);
    backdrop-filter: blur(4px);
    display: flex;
    justify-content: center;
    align-items: center;
}
.dr-loading-card {
    background: #fff;
    padding: 32px 44px;
    border-radius: 20px;
    text-align: center;
    border-bottom: 5px solid #EE7A22;
    min-width: 300px;
}
.dr-logo {
    width: 54px; height: 54px; border-radius: 14px;
    background: #EE7A22; color: #fff;
    display: inline-flex; align-items: center; justify-content: center;
    font-family: 'Sora', sans-serif; font-weight: 800; font-size: 20px;
    letter-spacing: -0.02em;
}
.dr-logo--lg { width: 64px; height: 64px; font-size: 24px; margin-bottom: 16px; animation: drPulse 1.2s ease-in-out infinite; }
@keyframes drPulse { 0%, 100% { transform: scale(1); } 50% { transform: scale(0.92); } }
.dr-loading-text { font-family: 'Sora', sans-serif; font-size: 18px; font-weight: 700; margin-bottom: 16px; }
.dr-loading-bar { width: 260px; height: 6px; background: #F1EEE9; border-radius: 3px; overflow: hidden; margin: 0 auto; }
.dr-loading-fill { height: 100%; background: #EE7A22; border-radius: 3px; transition: width 0.2s ease; }

.dr-fade-enter-active, .dr-fade-leave-active { transition: opacity 0.25s ease; }
.dr-fade-enter, .dr-fade-leave-to { opacity: 0; }

.dr-fade-up-enter-active { animation: drSlideUp 0.4s ease-out; }
@keyframes drSlideUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }

.dr-pop-enter-active { animation: drPopIn 0.28s cubic-bezier(0.34, 1.2, 0.64, 1); }
@keyframes drPopIn { from { transform: scale(0); opacity: 0; } to { transform: scale(1); opacity: 1; } }

/* ===================================================== */
/* HERO                                                    */
/* ===================================================== */
.dr-hero {
    background: #221F1C;
    color: #fff;
    border-radius: 20px;
    margin-bottom: 18px;
    overflow: hidden;
    border-bottom: 5px solid #EE7A22;
}
.dr-hero-grid {
    display: grid;
    grid-template-columns: minmax(0, 1.8fr) minmax(0, 1fr);
    gap: 28px;
    padding: 26px 30px;
    align-items: center;
}
.dr-hero-label {
    font-size: 13px;
    color: rgba(255, 255, 255, 0.6);
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.08em;
}
.dr-hero-title {
    font-family: 'Sora', sans-serif;
    font-size: 38px;
    font-weight: 800;
    line-height: 1.1;
    margin: 4px 0 12px;
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
}
.dr-count-pill {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: #EE7A22;
    color: #fff;
    font-family: 'Sora', sans-serif;
    font-size: 16px;
    font-weight: 800;
    padding: 4px 14px;
    border-radius: 999px;
}
.dr-hero-tags { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 12px; }
.dr-tag {
    display: inline-flex; align-items: center; gap: 6px;
    font-size: 12.5px; font-weight: 700;
    padding: 4px 12px; border-radius: 999px;
    background: rgba(255, 255, 255, 0.08);
    color: rgba(255, 255, 255, 0.85);
    border: 1px solid rgba(255, 255, 255, 0.14);
}
.dr-tag.is-orange { background: rgba(238, 122, 34, 0.22); color: #FFB06B; border-color: rgba(238, 122, 34, 0.45); }
.dr-tag.is-teal { background: rgba(15, 110, 107, 0.24); color: #7FDACF; border-color: rgba(15, 110, 107, 0.5); }
.dr-hero-sub {
    color: rgba(255, 255, 255, 0.62);
    font-size: 14.5px;
    line-height: 1.55;
    margin: 0;
    max-width: 60ch;
}
.dr-hero-qr {
    display: flex;
    align-items: center;
    gap: 16px;
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 16px;
    padding: 16px 20px;
    color: #FFB06B;
}
.dr-qr-title { font-family: 'Sora', sans-serif; font-weight: 700; font-size: 15px; color: #fff; }
.dr-qr-sub { font-size: 13px; color: rgba(255, 255, 255, 0.6); }

.dr-hero-actions {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
    padding: 14px 30px;
    background: #34302B;
}

@media (max-width: 900px) {
    .dr-hero-grid { grid-template-columns: 1fr; gap: 18px; }
    .dr-hero-title { font-size: 28px; }
    .dr-hero-actions { padding: 14px 20px; }
}

/* ===================================================== */
/* BUTTONS                                                 */
/* ===================================================== */
.dr-btn-primary.btn,
.dr-btn-ghost.btn,
.dr-btn-ghost-dark.btn {
    border-radius: 10px !important;
    font-weight: 700;
    font-size: 14px;
    padding: 8px 16px;
    display: inline-flex;
    align-items: center;
    gap: 7px;
    box-shadow: none !important;
}
.dr-btn-primary.btn {
    background: #EE7A22 !important;
    border: 1.5px solid #EE7A22 !important;
    color: #fff !important;
}
.dr-btn-primary.btn:hover:not(:disabled) {
    background: #D4620F !important;
    border-color: #D4620F !important;
}
.dr-btn-ghost.btn {
    background: #fff !important;
    color: #221F1C !important;
    border: 1.5px solid #E5E1DA !important;
}
.dr-btn-ghost.btn:hover:not(:disabled) { border-color: #221F1C !important; }
.dr-btn-ghost-dark.btn {
    background: rgba(255, 255, 255, 0.06) !important;
    color: #fff !important;
    border: 1.5px solid rgba(255, 255, 255, 0.22) !important;
}
.dr-btn-ghost-dark.btn:hover:not(:disabled) { border-color: #EE7A22 !important; }
.dr-page .btn:disabled { opacity: 0.5; }

/* ===================================================== */
/* CARD                                                    */
/* ===================================================== */
.dr-card {
    background: #fff;
    border: 1px solid #E5E1DA;
    border-radius: 18px;
    padding: 22px 24px;
    margin-bottom: 18px;
}

/* ===================================================== */
/* FILTER BAR                                              */
/* ===================================================== */
.dr-filter { padding: 18px 22px; }
.dr-filter-grid {
    display: grid;
    grid-template-columns: minmax(0, 2fr) minmax(0, 1fr) minmax(0, 1fr) auto;
    gap: 14px;
    align-items: end;
}
.dr-field { display: flex; flex-direction: column; gap: 6px; min-width: 0; }
.dr-field > span {
    font-size: 12.5px; font-weight: 700; color: #736C63;
    display: flex; align-items: center; gap: 6px;
    text-transform: uppercase; letter-spacing: 0.04em;
}
.dr-field > span .b-icon { color: #EE7A22; }
.dr-input, .dr-select {
    height: 42px;
    border: 1.5px solid #E5E1DA;
    border-radius: 10px;
    font-size: 15px;
    font-weight: 600;
    color: #221F1C;
    background: #F6F4F0;
}
.dr-input:focus, .dr-select:focus {
    border-color: #EE7A22;
    background: #fff;
    box-shadow: 0 0 0 3px rgba(238, 122, 34, 0.18);
}
.dr-field--action { min-width: 140px; }

@media (max-width: 900px) {
    .dr-filter-grid { grid-template-columns: 1fr 1fr; }
    .dr-field--search { grid-column: 1 / -1; }
    .dr-field--action { grid-column: 1 / -1; }
}
@media (max-width: 560px) {
    .dr-filter-grid { grid-template-columns: 1fr; }
}

/* ===================================================== */
/* EMPTY STATE                                             */
/* ===================================================== */
.dr-empty {
    text-align: center;
    padding: 60px 30px;
    border: 2px dashed #D6CFC4;
    background: #FAF9F7;
}
.dr-empty-icon {
    width: 78px; height: 78px; border-radius: 22px;
    background: #FFF1E4; color: #EE7A22;
    display: inline-flex; align-items: center; justify-content: center;
    font-size: 34px; margin-bottom: 18px;
}
.dr-empty h3 { font-family: 'Sora', sans-serif; font-size: 22px; font-weight: 700; margin: 0 0 8px; }
.dr-empty p { color: #736C63; max-width: 50ch; margin: 0 auto 20px; line-height: 1.6; }

/* ===================================================== */
/* DRAFT CARD LIST                                         */
/* ===================================================== */
.dr-list { display: flex; flex-direction: column; gap: 16px; }

.dr-pcard {
    position: relative;
    background: #fff;
    border: 1px solid #E5E1DA;
    border-left: 5px solid #EE7A22;
    border-radius: 18px;
    overflow: hidden;
    animation: drCardIn 0.4s ease-out backwards;
    transition: box-shadow 0.18s ease, transform 0.18s ease;
}
.dr-pcard:hover { box-shadow: 0 12px 28px -18px rgba(34, 31, 28, 0.4); }
.dr-pcard.is-complete { border-left-color: #0F6E6B; }
.dr-pcard.is-warning  { border-left-color: #C98A12; }
.dr-pcard.is-started  { border-left-color: #C93C30; }

@keyframes drCardIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
}

/* ---------- TOP: IDENTITY ---------- */
.dr-pcard-top {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 14px;
    flex-wrap: wrap;
    padding: 16px 20px;
    background: #221F1C;
    color: #fff;
}
.dr-pcard-id { display: flex; align-items: center; gap: 12px; }
.dr-pcard-id-icon {
    width: 42px; height: 42px; border-radius: 12px;
    background: rgba(238, 122, 34, 0.2);
    color: #FFB06B;
    display: inline-flex; align-items: center; justify-content: center;
    font-size: 18px;
}
.dr-pcard-id-label {
    font-size: 11px; color: rgba(255, 255, 255, 0.6);
    font-weight: 600; text-transform: uppercase; letter-spacing: 0.08em;
}
.dr-pcard-id-value {
    font-family: 'Sora', sans-serif;
    font-size: 20px; font-weight: 800;
    color: #FFB06B; letter-spacing: -0.01em;
    word-break: break-all;
}
.dr-pcard-badges { display: flex; flex-wrap: wrap; gap: 6px; }
.dr-chip {
    display: inline-flex; align-items: center; gap: 5px;
    font-size: 12px; font-weight: 700;
    padding: 4px 11px; border-radius: 999px;
    background: rgba(255, 255, 255, 0.1);
    color: #fff;
    border: 1px solid rgba(255, 255, 255, 0.16);
    white-space: nowrap;
}
.dr-chip.is-orange { background: rgba(238, 122, 34, 0.22); color: #FFB06B; border-color: rgba(238, 122, 34, 0.45); }
.dr-chip.is-teal { background: rgba(15, 110, 107, 0.24); color: #7FDACF; border-color: rgba(15, 110, 107, 0.5); }
.dr-chip.is-warn { background: rgba(201, 138, 18, 0.22); color: #FFD79A; border-color: rgba(201, 138, 18, 0.45); }
.dr-chip.is-danger { background: rgba(201, 60, 48, 0.22); color: #FFB0A5; border-color: rgba(201, 60, 48, 0.45); }

/* ---------- MIDDLE: PANELS ---------- */
.dr-pcard-body {
    display: grid;
    grid-template-columns: minmax(0, 1.4fr) minmax(0, 1.2fr) minmax(0, 0.9fr) minmax(0, 1fr);
    gap: 6px;
    padding: 6px;
}
.dr-pcard-panel {
    background: #F6F4F0;
    border-radius: 14px;
    padding: 14px 16px;
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 0;
}
.dr-panel-kicker {
    font-size: 11.5px;
    font-weight: 800;
    color: #736C63;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    display: flex;
    align-items: center;
    gap: 6px;
}
.dr-panel-kicker .b-icon { color: #EE7A22; }
.dr-panel-title {
    font-family: 'Sora', sans-serif;
    font-size: 17px; font-weight: 700;
    color: #221F1C; line-height: 1.3;
    word-break: break-word;
}
.dr-panel-line {
    font-size: 13.5px; color: #4A4540;
    display: flex; align-items: center; gap: 7px;
    word-break: break-word;
}
.dr-panel-line .b-icon { color: #736C63; flex-shrink: 0; }
.dr-panel-empty {
    font-size: 13.5px; color: #A59D92;
    display: flex; align-items: center; gap: 8px;
    font-style: italic;
}

.dr-meta-row {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 10px;
    font-size: 13.5px;
    padding: 3px 0;
    border-bottom: 1px dashed #E5E1DA;
}
.dr-meta-row:last-child { border-bottom: 0; }
.dr-meta-row span {
    color: #736C63; font-weight: 600;
    display: inline-flex; align-items: center; gap: 6px;
    white-space: nowrap;
}
.dr-meta-row span .b-icon { color: #EE7A22; font-size: 13px; }
.dr-meta-row b {
    font-weight: 700; color: #221F1C;
    text-align: right; word-break: break-word;
}

/* ---------- PROGRESS RING ---------- */
.dr-pcard-progress { align-items: center; text-align: center; justify-content: center; }
.dr-progress-ring {
    position: relative;
    width: 74px;
    height: 74px;
    margin: 6px auto 4px;
}
.dr-progress-svg {
    width: 100%; height: 100%;
    transform: rotate(-90deg);
}
.dr-progress-track {
    fill: none;
    stroke: #E5E1DA;
    stroke-width: 3.2;
}
.dr-progress-fill-circle {
    fill: none;
    stroke-width: 3.2;
    stroke-linecap: round;
    stroke-dasharray: 100.53;
    transition: stroke-dashoffset 0.6s cubic-bezier(0.2, 0.9, 0.4, 1.1);
}
.dr-progress-fill-circle.is-success { stroke: #0F6E6B; }
.dr-progress-fill-circle.is-info    { stroke: #2B83B5; }
.dr-progress-fill-circle.is-warning { stroke: #C98A12; }
.dr-progress-fill-circle.is-danger  { stroke: #C93C30; }
.dr-progress-value {
    position: absolute;
    inset: 0;
    display: flex; align-items: center; justify-content: center;
    font-family: 'Sora', sans-serif;
    font-size: 17px; font-weight: 800;
    color: #221F1C;
}
.dr-progress-label {
    font-family: 'Sora', sans-serif;
    font-size: 13px; font-weight: 700;
    color: #221F1C;
}
.dr-progress-sub { font-size: 12px; color: #736C63; }

/* ---------- AGREEMENTS ---------- */
.dr-pcard-agreements { gap: 8px; }
.dr-agreement-list { display: flex; flex-direction: column; gap: 5px; }
.dr-agreement-item {
    display: inline-flex; align-items: center; gap: 7px;
    font-size: 12.5px; font-weight: 700;
    padding: 4px 10px; border-radius: 8px;
}
.dr-agreement-item.is-ok { background: #E2F2F0; color: #0B5552; }
.dr-agreement-item.is-no { background: #FBE9E7; color: #8E2A21; }
.dr-agreement-empty {
    font-size: 12.5px; color: #A59D92; font-style: italic;
}

/* ---------- BOTTOM: ACTIONS ---------- */
.dr-pcard-actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
    padding: 14px 20px 16px;
    border-top: 1px dashed #E5E1DA;
    background: #FAF9F7;
}
.dr-actions-primary { display: flex; gap: 8px; flex-wrap: wrap; }
.dr-more-toggle {
    background: #fff !important;
    border: 1.5px solid #E5E1DA !important;
    border-radius: 10px !important;
    color: #221F1C !important;
    padding: 6px 12px !important;
    font-size: 16px;
    box-shadow: none !important;
}
.dr-more-toggle:hover { border-color: #221F1C !important; }
.dr-more .dropdown-menu { border-radius: 12px; border: 1px solid #E5E1DA; box-shadow: 0 12px 28px -14px rgba(34, 31, 28, 0.4); }
.dr-more .dropdown-item { font-size: 14px; padding: 8px 14px; font-weight: 600; }
.dr-more .dropdown-item:hover { background: #F6F4F0; color: #221F1C; }
.dr-more .dropdown-item.text-danger:hover { background: #FBE9E7; color: #C93C30 !important; }

@media (max-width: 1199px) {
    .dr-pcard-body { grid-template-columns: 1fr 1fr; }
    .dr-pcard-agreements { grid-column: 1 / -1; }
}
@media (max-width: 767px) {
    .dr-pcard-body { grid-template-columns: 1fr; }
    .dr-pcard-agreements { grid-column: auto; }
    .dr-pcard-id-value { font-size: 17px; }
    .dr-actions-primary { width: 100%; }
    .dr-actions-primary .btn { flex: 1; justify-content: center; }
    .dr-pcard-actions { padding: 12px 14px 14px; }
}

/* ===================================================== */
/* LIST TRANSITIONS                                        */
/* ===================================================== */
.dr-list-enter-active, .dr-list-leave-active { transition: opacity 0.25s ease, transform 0.25s ease; }
.dr-list-enter, .dr-list-leave-to { opacity: 0; transform: translateY(-8px); }
.dr-list-move { transition: transform 0.3s ease; }

/* ===================================================== */
/* PAGINATION                                              */
/* ===================================================== */
.dr-pagination-wrap {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 14px;
    flex-wrap: wrap;
    padding: 14px 22px;
}
.dr-pagination-info {
    font-size: 13.5px; color: #736C63; font-weight: 600;
    display: inline-flex; align-items: center; gap: 8px;
}
.dr-pagination-info .b-icon { color: #EE7A22; }
.dr-pagination >>> .page-item .page-link {
    border: 1.5px solid #E5E1DA;
    border-radius: 10px !important;
    margin: 0 3px;
    color: #221F1C;
    font-weight: 700;
    min-width: 38px;
    text-align: center;
    transition: all 0.15s ease;
}
.dr-pagination >>> .page-item .page-link:hover {
    border-color: #EE7A22;
    color: #D4620F;
}
.dr-pagination >>> .page-item.active .page-link {
    background: #EE7A22;
    border-color: #EE7A22;
    color: #fff;
}
.dr-pagination >>> .page-item.disabled .page-link { opacity: 0.4; }

/* ===================================================== */
/* MODAL                                                   */
/* ===================================================== */
.dr-modal.modal-content {
    border: 0;
    border-radius: 20px;
    overflow: hidden;
    box-shadow: 0 30px 70px -20px rgba(34, 31, 28, 0.5);
}
.dr-modal-header {
    background: #221F1C !important;
    color: #fff !important;
    border: 0 !important;
    border-bottom: 4px solid #EE7A22 !important;
    padding: 16px 24px !important;
}
.dr-modal-title { display: flex; align-items: center; gap: 14px; }
.dr-modal-title-icon {
    width: 42px; height: 42px; border-radius: 12px;
    background: rgba(238, 122, 34, 0.2);
    color: #FFB06B;
    display: inline-flex; align-items: center; justify-content: center;
    font-size: 18px;
}
.dr-modal-title-main { font-family: 'Sora', sans-serif; font-size: 19px; font-weight: 700; }
.dr-modal-title-sub { font-size: 13px; color: rgba(255, 255, 255, 0.65); }
.dr-modal .modal-header .close { color: #fff !important; opacity: 0.85; text-shadow: none; }

.dr-modal-agreement {
    background: #FFF1E4;
    border-bottom: 1px solid #F5CFAE;
    padding: 18px 24px;
}
.dr-modal-agreement-title {
    font-family: 'Sora', sans-serif;
    font-size: 14px;
    font-weight: 700;
    color: #D4620F;
    margin: 0 0 12px;
    display: flex;
    align-items: center;
    gap: 8px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
}
.dr-modal-agreement-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 12px;
    margin-bottom: 14px;
}
.dr-modal-agreement-item { display: flex; flex-direction: column; gap: 4px; }
.dr-modal-agreement-item .dr-chip {
    background: #fff;
    color: #221F1C;
    border: 1.5px solid #E5E1DA;
    font-size: 12.5px;
}
.dr-modal-agreement-item .dr-chip.is-teal { background: #E2F2F0; color: #0F6E6B; border-color: #BFE1DD; }
.dr-modal-agreement-item .dr-chip.is-warn { background: #FFF4DC; color: #8A5E09; border-color: #F2DBA5; }
.dr-modal-agreement-item .dr-chip.is-danger { background: #FBE9E7; color: #C93C30; border-color: #F3C9C3; }
.dr-modal-agreement-item small { font-size: 12px; color: #736C63; font-weight: 600; }
.dr-modal-signature {
    display: flex; align-items: center; gap: 12px; flex-wrap: wrap;
    padding-top: 12px; border-top: 1px dashed #F5CFAE;
}
.dr-modal-signature .dr-chip {
    background: #fff; color: #221F1C; border: 1.5px solid #E5E1DA;
}
.dr-modal-signature .dr-chip.is-teal { background: #E2F2F0; color: #0F6E6B; border-color: #BFE1DD; }
.dr-modal-signature .dr-chip.is-danger { background: #FBE9E7; color: #C93C30; border-color: #F3C9C3; }
.dr-modal-signature small { font-size: 12.5px; color: #736C63; font-weight: 600; }

.dr-modal-loading { text-align: center; padding: 60px 20px; color: #736C63; }
.dr-modal-loading p { margin-top: 14px; }

/* ===================================================== */
/* REDUCED MOTION                                          */
/* ===================================================== */
@media (prefers-reduced-motion: reduce) {
    .dr-pcard, .dr-logo--lg, .dr-count-pill { animation: none !important; }
    .dr-progress-fill-circle { transition: none; }
    .dr-list-move { transition: none; }
}
</style>


// WEBPACK FOOTER //
// src/components/Sales/ListDraftInvoices.vue